/**
 * DriversPage — Steward Admin
 * Manage drivers: add, edit, view list. Direct S3 photo upload.
 */
import React, { useState, useEffect } from 'react';
import {
  onDrivers, createDriver, updateDriver, deleteDriver, TITLES,
  onDriverContacts, linkDriverAccount, saveDriverContact, migrateDriverContacts,
} from '../services/tripleCrown';
import S3Upload from '../components/S3Upload';
import useChampionship from '../hooks/useChampionship';

function DriverModal({ driver, contact, championshipId, onClose, onSaved }) {
  const isEdit = !!driver;
  const [form, setForm] = useState({
    email: contact?.email || driver?.email || '',
    phone: contact?.phone || driver?.phone || '',
    name: driver?.name || '',
    age: driver?.age || '',
    team: driver?.team || '',
    photoUrl: driver?.photoUrl || '',
    titles: driver?.titles || TITLES.map(t => t.id),
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleTitle = (id) => {
    setForm(f => ({
      ...f,
      titles: f.titles.includes(id)
        ? f.titles.filter(t => t !== id)
        : [...f.titles, id],
    }));
  };

  // Generate a folder-safe ID for new drivers
  const uploadFolder = isEdit
    ? `championships/drivers/${driver.id}`
    : `championships/drivers/new-${Date.now()}`;

  const handleSave = async () => {
    if (!form.name.trim()) return;
    if (!championshipId) {
      alert('No active championship. Create one in Settings first.');
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: form.name.trim(),
        age: form.age ? parseInt(form.age) : null,
        team: form.team.trim(),
        photoUrl: form.photoUrl.trim(),
        titles: form.titles,
        championship: championshipId,
      };
      let id = driver?.id;
      if (isEdit) {
        await updateDriver(driver.id, data);
      } else {
        id = await createDriver({ ...data, status: 'active' });
      }
      /* Email and phone are private — saved by the server, not on the driver. */
      const e0 = contact?.email || driver?.email || '';
      const p0 = contact?.phone || driver?.phone || '';
      if (id && (form.email.trim() !== e0 || form.phone.trim() !== p0)) {
        await saveDriverContact({ driverId: id, email: form.email.trim(), phone: form.phone.trim() });
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{isEdit ? 'Edit Driver' : 'Add Driver'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Driver name" />
          </div>
          <div className="form-group">
            <label className="form-label">Age</label>
            <input className="form-input" type="number" value={form.age} onChange={e => set('age', e.target.value)} placeholder="Age" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email (private)</label>
            <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone (private)</label>
            <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Team</label>
          <input className="form-input" value={form.team} onChange={e => set('team', e.target.value)} placeholder="Team name" />
        </div>

        <div className="form-group">
          <label className="form-label">Driver Photo</label>
          <S3Upload
            folder={uploadFolder}
            accept="image/*"
            label="Upload Photo"
            currentUrl={form.photoUrl}
            onUploaded={({ publicUrl }) => set('photoUrl', publicUrl)}
          />
          {form.photoUrl && (
            <p className="form-hint" style={{ marginTop: 6, wordBreak: 'break-all' }}>{form.photoUrl}</p>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Titles</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TITLES.map(t => (
              <button
                key={t.id}
                type="button"
                className={`btn btn-sm ${form.titles.includes(t.id) ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => toggleTitle(t.id)}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Add Driver'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriversPage() {
  const { champ, loading } = useChampionship();
  const [drivers, setDrivers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);

  useEffect(() => {
    if (!champ) { setDrivers([]); return; }
    return onDrivers(champ.id, setDrivers);
  }, [champ]);

  /* Private contacts + account link (owner, 2 Nov). */
  const [contacts, setContacts] = useState({});
  useEffect(() => {
    if (!champ) { setContacts({}); return; }
    return onDriverContacts(champ.id, setContacts);
  }, [champ]);
  const contactOf = (d) => ({
    email: (contacts[d.id] || {}).email || d.email || '',
    phone: (contacts[d.id] || {}).phone || d.phone || '',
  });
  const unmigrated = drivers.filter(d => d.email || d.phone).length;
  const [migrating, setMigrating] = useState(false);
  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const r = await migrateDriverContacts();
      alert(`Moved contact details for ${(r.data && r.data.moved) || 0} drivers to private storage.`);
    } catch (err) { alert(err.message || 'Could not move contact details'); }
    setMigrating(false);
  };
  /* Same email / phone / account on two drivers — shown to the stewards. */
  const dupWarnings = (d) => {
    const c = contactOf(d);
    const digits = String(c.phone || '').replace(/\D/g, '');
    const out = [];
    drivers.forEach(o => {
      if (o.id === d.id) return;
      const oc = contactOf(o);
      if (c.email && oc.email && c.email.toLowerCase() === oc.email.toLowerCase()) out.push(`Same email as ${o.name}`);
      if (digits && String(oc.phone || '').replace(/\D/g, '') === digits) out.push(`Same phone as ${o.name}`);
      if (d.uid && o.uid === d.uid) out.push(`Same account as ${o.name}`);
      if ((d.name || '').trim().toLowerCase() === (o.name || '').trim().toLowerCase()) out.push(`Same name as ${o.name}`);
    });
    return [...new Set(out)];
  };
  const handleLink = async (d) => {
    const id = window.prompt(`Link ${d.name} to a Whapaxx account.\nEnter the account email or Whapaxx ID:`);
    if (!id || !id.trim()) return;
    try {
      const r = await linkDriverAccount({ driverId: d.id, identifier: id.trim() });
      const a = r.data || {};
      alert(`Linked to ${a.accountName || a.email || 'the account'}.`);
    } catch (err) { alert(err.message || 'Could not link'); }
  };
  const handleUnlink = async (d) => {
    if (!window.confirm(`Unlink ${d.name} from their Whapaxx account? They won't be able to play limited titles until relinked.`)) return;
    try { await linkDriverAccount({ driverId: d.id, unlink: true }); } catch (err) { alert(err.message || 'Could not unlink'); }
  };
  const accountCell = (d) => (
    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
      {d.uid ? (
        <>
          <span className="badge badge-success" title={d.uid}>Linked</span>
          {d.accountName && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{d.accountName}</div>}
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 4 }} onClick={() => handleLink(d)}>Relink</button>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 4, marginLeft: 4 }} onClick={() => handleUnlink(d)}>Unlink</button>
        </>
      ) : (
        <>
          <span className="badge badge-pink">Not linked</span>
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 6 }} onClick={() => handleLink(d)}>Link account</button>
        </>
      )}
    </td>
  );
  const nameCell = (d) => {
    const warn = dupWarnings(d);
    return (
      <td style={{ fontWeight: 600 }}>
        {d.name}
        {d.autoApproved && (
          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>Self-registered · auto-approved</div>
        )}
        {d.nameMismatch && d.accountName && (
          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--gold, #e0b000)' }}>Account name: {d.accountName}</div>
        )}
        {warn.map(w => (
          <div key={w} style={{ fontSize: 11, fontWeight: 400, color: 'var(--pink, #ff5a8a)' }}>⚠ {w}</div>
        ))}
      </td>
    );
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingDriver(null);
    setModalOpen(true);
  };

  const handleWithdraw = async (driver) => {
    if (window.confirm(`Withdraw ${driver.name} from the championship?`)) {
      await updateDriver(driver.id, { status: 'withdrawn' });
    }
  };

  const handleActivate = async (driver) => {
    await updateDriver(driver.id, { status: 'active' });
  };

  const handleDelete = async (driver) => {
    if (window.confirm(`Permanently delete ${driver.name}? This cannot be undone.`)) {
      await deleteDriver(driver.id);
    }
  };

  const pendingDrivers = drivers.filter(d => d.status === 'pending');
  const activeDrivers = drivers.filter(d => d.status !== 'pending');

  const handleApprove = async (driver) => {
    // Ensure titles are set — website signups may be missing them if
    // the Cloud Function wasn't redeployed with the titles default.
    const updates = { status: 'active' };
    if (!driver.titles || driver.titles.length === 0) {
      updates.titles = TITLES.map(t => t.id);
    }
    await updateDriver(driver.id, updates);
  };

  const handleReject = async (driver) => {
    if (window.confirm(`Reject registration for ${driver.name}?`)) {
      await updateDriver(driver.id, { status: 'rejected' });
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Drivers</h1>
          <p className="page-subtitle">
            {activeDrivers.filter(d => d.status === 'active').length} active
            {pendingDrivers.length > 0 && ` · ${pendingDrivers.length} pending`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleAdd} disabled={!champ}>
          {loading ? 'Loading...' : !champ ? 'No Championship' : '+ Add Driver'}
        </button>
      </div>

      {unmigrated > 0 && (
        <div className="card" style={{ marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>{unmigrated} driver{unmigrated === 1 ? '' : 's'} still have email/phone on the public driver record.</span>
          <button className="btn btn-primary btn-sm" disabled={migrating} onClick={handleMigrate}>
            {migrating ? 'Moving…' : 'Make contact details private'}
          </button>
        </div>
      )}

      {/* Pending Registrations */}
      {pendingDrivers.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <h3 className="card-title">Pending Registrations</h3>
            <span className="badge badge-gold">{pendingDrivers.length} awaiting review</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Account</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Team</th>
                  <th>Age</th>
                  <th>Notify</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendingDrivers.map(d => (
                  <tr key={d.id}>
                    <td style={{ width: 40 }}>
                      {d.photoUrl ? (
                        <img src={d.photoUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                          {d.name?.[0] || '?'}
                        </div>
                      )}
                    </td>
                    {nameCell(d)}
                    {accountCell(d)}
                    <td style={{ fontSize: 13 }}>{contactOf(d).email || '—'}</td>
                    <td style={{ fontSize: 13 }}>{contactOf(d).phone || '—'}</td>
                    <td>{d.team || '—'}</td>
                    <td>{d.age || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {d.notifications?.email && <span className="badge badge-teal" style={{ marginRight: 3 }}>Email</span>}
                      {d.notifications?.whatsApp && <span className="badge badge-success">WA</span>}
                      {!d.notifications?.email && !d.notifications?.whatsApp && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-primary btn-sm" style={{ marginRight: 6 }} onClick={() => handleApprove(d)}>Approve</button>
                      <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => handleEdit(d)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleReject(d)}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active / Other Drivers */}
      {activeDrivers.length === 0 && pendingDrivers.length === 0 ? (
        <div className="empty-state">
          <h3>No drivers yet</h3>
          <p>Add drivers to the championship or wait for signups</p>
        </div>
      ) : activeDrivers.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Account</th>
                  <th>Email</th>
                  <th>Team</th>
                  <th>Age</th>
                  <th>Titles</th>
                  <th>Status</th>
                  <th>Notify</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {activeDrivers.map(d => (
                  <tr key={d.id}>
                    <td style={{ width: 40 }}>
                      {d.photoUrl ? (
                        <img src={d.photoUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                          {d.name?.[0] || '?'}
                        </div>
                      )}
                    </td>
                    {nameCell(d)}
                    {accountCell(d)}
                    <td style={{ fontSize: 13 }}>{contactOf(d).email || '—'}</td>
                    <td>{d.team || '—'}</td>
                    <td>{d.age || '—'}</td>
                    <td>
                      {(d.titles || []).map(tid => {
                        const t = TITLES.find(x => x.id === tid);
                        return <span key={tid} className="badge badge-teal" style={{ marginRight: 4 }}>{t?.short || tid}</span>;
                      })}
                    </td>
                    <td>
                      <span className={`badge ${d.status === 'active' ? 'badge-success' : d.status === 'rejected' ? 'badge-pink' : 'badge-muted'}`}>
                        {d.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {d.notifications?.email && <span className="badge badge-teal" style={{ marginRight: 3 }}>Email</span>}
                      {d.notifications?.whatsApp && <span className="badge badge-success">WA</span>}
                      {!d.notifications?.email && !d.notifications?.whatsApp && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {d.status !== 'active' && (
                        <button className="btn btn-primary btn-sm" style={{ marginRight: 6 }} onClick={() => handleActivate(d)}>Activate</button>
                      )}
                      <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => handleEdit(d)}>Edit</button>
                      {d.status === 'active' && (
                        <button className="btn btn-danger btn-sm" style={{ marginRight: 6 }} onClick={() => handleWithdraw(d)}>Withdraw</button>
                      )}
                      <button className="btn btn-danger btn-sm" style={{ opacity: 0.6 }} onClick={() => handleDelete(d)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <DriverModal
          driver={editingDriver}
          contact={editingDriver ? contacts[editingDriver.id] : null}
          championshipId={champ?.id}
          onClose={() => setModalOpen(false)}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}
